// 🧾➖ Aplicar Notas de Crédito — Gestión CxC / CxP
// Empareja cada NC con su factura destino (campo numDocModificado del XML) y la
// aplica rebajando el saldo de la factura en cartera (registra un movimiento de
// abono forma_pago='nota_credito'). Aplicación individual o masiva, reversible.
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, FileMinus, FileText, Filter, Link2, RotateCcw, type LucideIcon } from 'lucide-react';
import { VincularFacturaModal } from './VincularFactura';
import { SortableTh, useSort } from './tablaSort';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
// Ruta del archivo en disco → URL servida (igual que en CarteraFacturas/ProcesarDocumentos)
const urlDoc = (p: string | null) => (p ? p.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

interface ModoNc {
  titulo: string;
  descripcion: string;
  icono: LucideIcon;
  contraparte: string;
  rpcListar: string;
  rpcAplicar: string;
  rpcDesaplicar: string;
  rpcMasivo: string;
}

const MODO_CXC: ModoNc = {
  titulo: 'Notas de Crédito Emitidas',
  descripcion: 'Notas de crédito emitidas aplicadas a las facturas por cobrar',
  icono: FileMinus,
  contraparte: 'Cliente',
  rpcListar: 'cxc_nc_listar',
  rpcAplicar: 'cxc_nc_aplicar',
  rpcDesaplicar: 'cxc_nc_desaplicar',
  rpcMasivo: 'cxc_nc_aplicar_masivo',
};

const MODO_CXP: ModoNc = {
  titulo: 'Notas de Crédito Recibidas',
  descripcion: 'Notas de crédito recibidas aplicadas a las facturas por pagar',
  icono: FileMinus,
  contraparte: 'Proveedor',
  rpcListar: 'cxp_nc_listar',
  rpcAplicar: 'cxp_nc_aplicar',
  rpcDesaplicar: 'cxp_nc_desaplicar',
  rpcMasivo: 'cxp_nc_aplicar_masivo',
};

export function CxcNotasCredito() { return <AplicarNotasCredito cfg={MODO_CXC} />; }
export function CxpNotasCredito() { return <AplicarNotasCredito cfg={MODO_CXP} />; }

interface NotaCredito {
  nc_id: string;
  nc_serie: string;
  fecha_emision: string | null;
  emisor?: string | null;
  receptor?: string | null;
  ruc_emisor?: string | null;
  ruc_receptor?: string | null;
  num_doc_modificado: string | null;
  monto: number;
  factura_ticket_id: string | null;
  factura_serie: string | null;
  factura_importe: number | null;
  xml_url: string | null;
  ride_url: string | null;
  estado: 'aplicada' | 'pendiente' | 'sin_factura';
}

const ESTADO_CHIP: Record<string, string> = {
  aplicada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  sin_factura: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const ESTADO_LABEL: Record<string, string> = {
  aplicada: 'Aplicada', pendiente: 'Pendiente', sin_factura: 'Sin factura',
};
const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

const ESTADOS_KPI = [
  { k: 'pendiente', label: 'Pendientes', cls: 'text-yellow-700 dark:text-yellow-300' },
  { k: 'aplicada', label: 'Aplicadas', cls: 'text-green-700 dark:text-green-300' },
  { k: 'sin_factura', label: 'Sin factura destino', cls: 'text-red-700 dark:text-red-300' },
] as const;

function AplicarNotasCredito({ cfg }: { cfg: ModoNc }) {
  const queryClient = useQueryClient();
  const anioActual = new Date().getFullYear();
  const Icono = cfg.icono;

  const [filtros, setFiltros] = useState({ estado: 'pendientes', anio: '', mes: '', serie: '' });
  const [aplicados, setAplicados] = useState(filtros);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());

  const { data: notas = [], isFetching } = useQuery<NotaCredito[]>({
    queryKey: ['ncListar', cfg.rpcListar, aplicados],
    queryFn: async () => {
      const { data } = await apiClient.post(`/rpc/${cfg.rpcListar}`, {
        _estado: aplicados.estado,
        _anio: aplicados.anio ? Number(aplicados.anio) : null,
        _mes: aplicados.mes ? Number(aplicados.mes) : null,
        _serie: aplicados.serie || null,
      }, FIN);
      return data || [];
    },
  });

  const refrescar = () => {
    void queryClient.invalidateQueries({ queryKey: ['ncListar'] });
    void queryClient.invalidateQueries({ queryKey: ['cartera'] }); // CxC/CxP saldos
    setSeleccion(new Set());
  };

  // Desglose por estado (todos los estados del período, sin importar el filtro de estado)
  const { data: notasKpi = [] } = useQuery<NotaCredito[]>({
    queryKey: ['ncKpi', cfg.rpcListar, aplicados.anio, aplicados.mes, aplicados.serie],
    queryFn: async () => {
      const { data } = await apiClient.post(`/rpc/${cfg.rpcListar}`, {
        _estado: 'todas',
        _anio: aplicados.anio ? Number(aplicados.anio) : null,
        _mes: aplicados.mes ? Number(aplicados.mes) : null,
        _serie: aplicados.serie || null,
      }, FIN);
      return data || [];
    },
  });
  const kpis = ESTADOS_KPI.map((e) => {
    const items = notasKpi.filter((n) => n.estado === e.k);
    return { ...e, n: items.length, monto: items.reduce((s, n) => s + Number(n.monto || 0), 0) };
  });

  const esCxc = cfg.contraparte === 'Cliente';
  const [vinculando, setVinculando] = useState<NotaCredito | null>(null);
  const vincular = useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/rpc/${esCxc ? 'cxc' : 'cxp'}_nc_vincular_factura`,
        { _nc_id: vinculando!.nc_id, _ticket_id: ticketId }, FIN)).data,
    onSuccess: () => { toast.success('Factura vinculada'); setVinculando(null); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo vincular'),
  });

  const ncSort = useSort<NotaCredito>(notas);
  const aplicables = notas.filter((n) => n.estado === 'pendiente');
  const todosSel = aplicables.length > 0 && aplicables.every((n) => seleccion.has(n.nc_id));

  // 984: antes de aplicar se consulta el caso. Normal o redondeo (exceso ≤ 0,05) → se
  // aplica directo; NC mayor que el saldo → panel de autorización (nivel 5); NC mayor
  // que el TOTAL de la factura → bloqueada (la NC está mal emitida).
  const [exceso, setExceso] = useState<{ ncId: string; info: ExcesoInfo } | null>(null);
  const aplicarUna = useMutation({
    mutationFn: async (v: { ncId: string; destino?: string; motivoCodigo?: string; motivo?: string; facturaDestino?: string }) =>
      v.destino === 'otra_factura'
        // 986: la NC se aplica a su factura y el saldo a favor, en el mismo paso, a la factura elegida
        ? (await apiClient.post('/rpc/fin_nc_aplicar_a_otra', {
            _lado: esCxc ? 'cxc' : 'cxp', _nc_id: v.ncId, _factura_destino: v.facturaDestino, _motivo_codigo: v.motivoCodigo, _motivo: v.motivo,
          }, FIN)).data
        : (await apiClient.post(`/rpc/${cfg.rpcAplicar}`, {
            _nc_id: v.ncId, ...(v.destino ? { _destino: v.destino, _motivo_codigo: v.motivoCodigo, _motivo: v.motivo } : {}),
          }, FIN)).data,
    onSuccess: (r: any) => { toast.success(r?.factura_destino ? r.message : `${r?.message} (${usd(r?.aplicado ?? r?.monto)})`); setExceso(null); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo aplicar'),
  });
  const intentarAplicar = async (ncId: string) => {
    try {
      const info: ExcesoInfo = (await apiClient.post('/rpc/fin_nc_exceso_info', { _lado: esCxc ? 'cxc' : 'cxp', _nc_id: ncId }, FIN)).data;
      if (info.caso === 'requiere_autorizacion') { setExceso({ ncId, info }); return; }
      if (info.caso === 'supera_total') { void dlg.avisar('No se puede aplicar esta nota de crédito', info.motivo ?? ''); return; }
      aplicarUna.mutate({ ncId });
    } catch (e: any) { toast.error(e?.response?.data?.message || 'No se pudo consultar la nota de crédito'); }
  };

  const desaplicar = useMutation({
    mutationFn: async (ncId: string) =>
      (await apiClient.post(`/rpc/${cfg.rpcDesaplicar}`, { _nc_id: ncId }, FIN)).data,
    onSuccess: () => { toast.success('Aplicación revertida'); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo revertir'),
  });

  const aplicarMasivo = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/rpc/${cfg.rpcMasivo}`, { _nc_ids: [...seleccion] }, FIN)).data,
    onSuccess: (r: any) => {
      if (r.errores?.length) toast.warning(`${r.aplicadas} aplicadas · ${r.errores.length} con error (${r.errores[0].error})`);
      else toast.success(`${r.aplicadas} notas de crédito aplicadas`);
      refrescar();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo aplicar el lote'),
  });

  const toggle = (id: string) => setSeleccion((p) => {
    const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const contraparteDe = (n: NotaCredito) => cfg.contraparte === 'Cliente'
    ? `${n.receptor || ''} ${n.ruc_receptor || ''}`.trim()
    : `${n.emisor || ''} ${n.ruc_emisor || ''}`.trim();

  // Exportar (CSV / XLS / PDF) — respeta filtros y orden actuales
  const nombreArchivo = `notas-credito-${esCxc ? 'cxc' : 'cxp'}-${fechaLocal()}`;
  const headersExp = ['N°', 'Fecha', 'NC', cfg.contraparte, 'Factura que modifica', 'Monto', 'Estado'];
  const filasExport: Celda[][] = [
    ...ncSort.sorted.map((n, i): Celda[] => [
      i + 1, n.fecha_emision ?? '', n.nc_serie ?? '', contraparteDe(n) || '', n.num_doc_modificado ?? '', Number(n.monto || 0), n.estado ?? '',
    ]),
    ['', '', '', '', 'TOTAL', ncSort.sorted.reduce((a, n) => a + Number(n.monto || 0), 0), ''],
  ];

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-6">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <Icono className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{cfg.titulo}</h1>
          <p className="text-sm text-muted-foreground">{cfg.descripcion}</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="nc-estado">Estado</label>
              <select id="nc-estado" className={selectCls} value={filtros.estado}
                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                <option value="todas">Todas</option>
                <option value="pendientes">Pendientes (con factura)</option>
                <option value="aplicadas">Aplicadas</option>
                <option value="sin_factura">Sin factura destino</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="nc-anio">Año</label>
              <select id="nc-anio" className={selectCls} value={filtros.anio}
                onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}>
                <option value="">Todos</option>
                {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="nc-mes">Mes</label>
              <select id="nc-mes" className={selectCls} value={filtros.mes}
                onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                <option value="">Todos</option>
                {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="nc-serie">Serie</label>
              <input id="nc-serie" className={selectCls} placeholder="NC o factura…"
                value={filtros.serie}
                onChange={(e) => setFiltros({ ...filtros, serie: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setAplicados(filtros); setSeleccion(new Set()); } }} />
            </div>
            <Button size="sm" onClick={() => { setAplicados(filtros); setSeleccion(new Set()); }} isLoading={isFetching}>
              {!isFetching && <Filter className="w-4 h-4" />}
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs por estado (monto $) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {kpis.map((e) => (
          <Card key={e.k}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground">{e.label}</p>
              <p className={`text-2xl font-bold ${e.cls}`}>{usd(e.monto)}</p>
              <p className="text-xs text-muted-foreground">{e.n} nota{e.n === 1 ? '' : 's'} de crédito</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Barra masiva */}
      {seleccion.size > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold">{seleccion.size} seleccionada{seleccion.size === 1 ? '' : 's'}</p>
            <Button size="sm" isLoading={aplicarMasivo.isPending} onClick={() => aplicarMasivo.mutate()}>
              {!aplicarMasivo.isPending && <CheckCircle2 className="w-4 h-4" />}
              Aplicar {seleccion.size}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>Limpiar</Button>
          </CardContent>
        </Card>
      )}

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Notas de crédito ({notas.length})</CardTitle>
            <ExportarBotones
              nombre={nombreArchivo} titulo={cfg.titulo} headers={headersExp} filas={filasExport}
              colsDerecha={[0, 5]} subtitulo={`${new Date().toLocaleString(LOCALE)} · ${ncSort.sorted.length} nota(s) de crédito`}
              fmtPDF={(v, c) => (c === 5 ? usd(Number(v)) : String(v ?? ''))}
              disabled={ncSort.sorted.length === 0} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right w-12">N°</th>
                <th className="py-2 pr-2 w-8">
                  <input type="checkbox" className="h-4 w-4 rounded border-input align-middle"
                    checked={todosSel}
                    onChange={() => setSeleccion(todosSel ? new Set() : new Set(aplicables.map((n) => n.nc_id)))}
                    title="Seleccionar todas las aplicables" />
                </th>
                <SortableTh col="fecha_emision" label="Fecha" sort={ncSort.sort} onSort={ncSort.toggle} />
                <SortableTh col="nc_serie" label="NC" sort={ncSort.sort} onSort={ncSort.toggle} />
                <SortableTh col={cfg.contraparte === 'Cliente' ? 'receptor' : 'emisor'} label={cfg.contraparte} sort={ncSort.sort} onSort={ncSort.toggle} />
                <SortableTh col="num_doc_modificado" label="Factura que modifica" sort={ncSort.sort} onSort={ncSort.toggle} />
                <SortableTh col="monto" label="Monto" align="right" sort={ncSort.sort} onSort={ncSort.toggle} />
                <th className="py-2 pr-3">Comprobante</th>
                <SortableTh col="estado" label="Estado" sort={ncSort.sort} onSort={ncSort.toggle} />
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {ncSort.sorted.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">
                  Sin notas de crédito para los filtros seleccionados
                </td></tr>
              )}
              {ncSort.sorted.map((n, idx) => (
                <Fragment key={n.nc_id}>
                <tr className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{idx + 1}</td>
                  <td className="py-2 pr-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-input align-middle"
                      checked={seleccion.has(n.nc_id)}
                      disabled={n.estado !== 'pendiente'}
                      onChange={() => toggle(n.nc_id)} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{n.fecha_emision || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{n.nc_serie}</td>
                  <td className="py-2 pr-3">
                    <p className="truncate max-w-56" title={contraparteDe(n)}>{contraparteDe(n) || '—'}</p>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {n.num_doc_modificado || '—'}
                    {n.factura_importe != null && (
                      <span className="text-xs text-muted-foreground"> ({usd(n.factura_importe)})</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(n.monto)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {urlDoc(n.xml_url) && (
                      <a href={urlDoc(n.xml_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline" title="Ver XML">
                        <Download className="w-4 h-4" /> XML
                      </a>
                    )}
                    {urlDoc(n.ride_url) && (
                      <a href={urlDoc(n.ride_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline" title="Ver RIDE">
                        <FileText className="w-4 h-4" /> RIDE
                      </a>
                    )}
                    {!urlDoc(n.xml_url) && !urlDoc(n.ride_url) && <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CHIP[n.estado]}`}
                      title={n.estado === 'sin_factura' ? 'La factura no está en cartera (descárguela y procésela)' : ''}>
                      {ESTADO_LABEL[n.estado]}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {n.estado === 'pendiente' && exceso?.ncId !== n.nc_id && (
                      <Button size="sm" isLoading={aplicarUna.isPending && aplicarUna.variables?.ncId === n.nc_id} onClick={() => void intentarAplicar(n.nc_id)}>
                        Aplicar
                      </Button>
                    )}
                    {n.estado === 'aplicada' && (
                      <Button size="sm" variant="outline" isLoading={desaplicar.isPending}
                        onClick={async () => {
                          if (await dlg.confirmar(`¿Revertir la aplicación de la NC ${n.nc_serie}?`, { detalle: 'El saldo de la factura volverá a subir.' })) {
                            desaplicar.mutate(n.nc_id);
                          }
                        }}>
                        <RotateCcw className="w-3.5 h-3.5" /> Revertir
                      </Button>
                    )}
                    {n.estado === 'sin_factura' && (
                      <Button size="sm" variant="outline" onClick={() => setVinculando(n)}>
                        <Link2 className="w-3.5 h-3.5" /> Vincular factura
                      </Button>
                    )}
                  </td>
                </tr>
                {exceso?.ncId === n.nc_id && (
                  <tr className="border-b"><td colSpan={10} className="bg-muted/20 p-2">
                    <PanelExceso info={exceso.info} contraparte={cfg.contraparte} enviando={aplicarUna.isPending}
                      lado={esCxc ? 'cxc' : 'cxp'} ncId={n.nc_id}
                      onCancelar={() => setExceso(null)}
                      onConfirmar={(destino, motivoCodigo, motivo, facturaDestino) => aplicarUna.mutate({ ncId: n.nc_id, destino, motivoCodigo, motivo, facturaDestino })} />
                  </td></tr>
                )}
                </Fragment>
              ))}
              {ncSort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3 text-right" colSpan={6}>Totales ({ncSort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(ncSort.sorted.reduce((a, n) => a + Number(n.monto || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {vinculando && (
        <VincularFacturaModal
          facturasRpc={esCxc ? 'cxc_listar' : 'cxp_listar'}
          contraparte={cfg.contraparte}
          detalle={`NC ${vinculando.nc_serie} · ${contraparteDe(vinculando)} · ${usd(vinculando.monto)} — elige la factura que modifica.`}
          vinculando={vincular.isPending}
          onClose={() => setVinculando(null)}
          onPick={(ticketId) => vincular.mutate(ticketId)}
        />
      )}
    </div>
  );
}

// ── 984 · Panel de autorización: NC mayor que el saldo de la factura ─────────
interface ExcesoInfo {
  caso: 'normal' | 'redondeo' | 'requiere_autorizacion' | 'supera_total' | 'sin_factura';
  total: number; saldo: number; monto: number; exceso: number; ya_nc: number; factura: string; nc: string;
  motivo?: string; nivel5: boolean; motivos: string[];
}
const MOTIVO_LABEL: Record<string, string> = {
  descuento_posterior: 'Descuento posterior a la factura',
  devolucion_ya_cobrada: 'Devolución de algo ya cobrado / pagado',
  error_emision: 'Error en la factura original',
  otro: 'Otro',
};
function PanelExceso({ info, contraparte, enviando, lado, ncId, onCancelar, onConfirmar }: {
  info: ExcesoInfo; contraparte: string; enviando: boolean; lado: 'cxc' | 'cxp'; ncId: string; onCancelar: () => void;
  onConfirmar: (destino: string, motivoCodigo: string, motivo: string, facturaDestino?: string) => void;
}) {
  const [destino, setDestino] = useState('anticipo');
  const [facturaDestino, setFacturaDestino] = useState('');
  // 986: facturas del mismo tercero con saldo, para aplicar ahí el saldo a favor
  const { data: destinos = [], isLoading: cargandoDestinos } = useQuery<{ id: string; serie: string; fecha: string; total: number; saldo: number }[]>({
    queryKey: ['fin-nc-destinos', lado, ncId],
    queryFn: async () => (await apiClient.post('/rpc/fin_nc_facturas_destino', { _lado: lado, _nc_id: ncId }, FIN)).data,
    enabled: destino === 'otra_factura',
  });
  const elegida = destinos.find((d) => d.id === facturaDestino);
  const [motivoCodigo, setMotivoCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const quien = contraparte === 'Cliente' ? 'del cliente' : 'del proveedor';
  const valido = info.nivel5 && !!motivoCodigo && motivo.trim().length >= 15 && (destino !== 'otra_factura' || !!facturaDestino);
  return (
    <div className="space-y-3 rounded-md border-2 border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/20">
      <p className="font-medium">La NC {info.nc} supera el saldo pendiente de la factura {info.factura}: requiere autorización de un nivel 5</p>
      <div className="grid gap-2 sm:grid-cols-5">
        {[['Total de la factura', info.total], ['Ya cobrado / pagado', Number(info.total) - Number(info.saldo)], ['Saldo pendiente', info.saldo],
          ['Valor de la NC', info.monto], ['Sobrante (saldo a favor)', info.exceso]].map(([t, v]) => (
          <div key={String(t)} className="rounded border bg-background p-2"><div className="text-xs text-muted-foreground">{t}</div><div className="font-semibold tabular-nums">{usd(Number(v))}</div></div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Se aplicará {usd(info.saldo)} a la factura (queda en $0) y el sobrante de {usd(info.exceso)} quedará como saldo a favor {quien}.</p>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1">
          <p className="text-xs font-medium">¿Qué hacer con el sobrante?</p>
          <label className="flex items-center gap-2"><input type="radio" checked={destino === 'anticipo'} onChange={() => setDestino('anticipo')} /> Saldo a favor (anticipo) para otra factura</label>
          <label className="flex items-center gap-2"><input type="radio" checked={destino === 'otra_factura'} onChange={() => setDestino('otra_factura')} /> Aplicar el saldo a favor a otra factura</label>
          <label className="flex items-center gap-2"><input type="radio" checked={destino === 'devolucion'} onChange={() => setDestino('devolucion')} /> Devolución pendiente (se devolverá el dinero)</label>
          {destino === 'otra_factura' && (
            <div className="space-y-1 pt-1">
              <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={facturaDestino} onChange={(e) => setFacturaDestino(e.target.value)} aria-label="Factura destino">
                <option value="">{cargandoDestinos ? 'Cargando facturas…' : destinos.length ? `— Elija la factura (${destinos.length} con saldo) —` : 'No hay otras facturas con saldo'}</option>
                {destinos.map((d) => <option key={d.id} value={d.id}>{d.serie} · {String(d.fecha).slice(0, 10)} · saldo {usd(d.saldo)}</option>)}
              </select>
              {elegida && (
                <p className="text-xs text-muted-foreground">
                  Se aplicarán {usd(Math.min(Number(info.exceso), Number(elegida.saldo)))} a la factura {elegida.serie}
                  {Number(info.exceso) > Number(elegida.saldo) + 0.005 ? ` y quedarán ${usd(Number(info.exceso) - Number(elegida.saldo))} como saldo a favor` : ''}.
                </p>
              )}
            </div>
          )}
        </div>
        <label className="space-y-1 text-xs font-medium">Motivo *
          <select className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm font-normal" value={motivoCodigo} onChange={(e) => setMotivoCodigo(e.target.value)}>
            <option value="">— Seleccione —</option>
            {info.motivos.map((m) => <option key={m} value={m}>{MOTIVO_LABEL[m] ?? m}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-xs font-medium">Explicación * (mín. 15 caracteres)
          <textarea className="mt-1 min-h-[2.25rem] w-full rounded-md border bg-background px-2 py-1 text-sm font-normal" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
      </div>
      {!info.nivel5 && <p className="text-xs font-medium text-red-700 dark:text-red-400">Solo un usuario de nivel 5 (administrador) puede autorizar esta aplicación. Pídaselo a un administrador.</p>}
      <div className="flex gap-2">
        <Button size="sm" disabled={!valido} isLoading={enviando} onClick={() => onConfirmar(destino, motivoCodigo, motivo.trim(), facturaDestino || undefined)}>Autorizar y aplicar</Button>
        <Button size="sm" variant="ghost" onClick={onCancelar}>Cancelar</Button>
      </div>
    </div>
  );
}
